<script>
import { useAlert, useTrack } from 'dashboard/composables';
import ReportFilters from './ReportFilters.vue';
import ReportContainer from '../ReportContainer.vue';
import ConversationsListTable from './ConversationsListTable.vue';
import { GROUP_BY_FILTER } from '../constants';
import { generateFileName } from '../../../../../helper/downloadHelper';
import { REPORTS_EVENTS } from '../../../../../helper/AnalyticsHelper/events';
import { mapGetters } from 'vuex';

const GROUP_BY_OPTIONS = {
  DAY: [{ id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' }],
  WEEK: [
    { id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' },
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
  ],
  MONTH: [
    { id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' },
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
    { id: 3, groupByKey: 'REPORT.GROUPING_OPTIONS.MONTH' },
  ],
  YEAR: [
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
    { id: 3, groupByKey: 'REPORT.GROUPING_OPTIONS.MONTH' },
  ],
};

export default {
  components: {
    ReportFilters,
    ReportContainer,
    ConversationsListTable,
  },
  props: {
    type: {
      type: String,
      default: 'account',
    },
    getterKey: {
      type: String,
      default: '',
    },
    actionKey: {
      type: String,
      default: '',
    },
    downloadButtonLabel: {
      type: String,
      default: 'Download Reports',
    },
  },
  data() {
    return {
      from: 0,
      to: 0,
      selectedFilter: null,
      groupBy: GROUP_BY_FILTER[1],
      groupByfilterItemsList: GROUP_BY_OPTIONS.DAY.map(this.translateOptions),
      selectedGroupByFilter: null,
      businessHours: false,
      showDropdown: false, // Added for dropdown visibility
      conversationsPageIndex: 0, // For conversations list pagination
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
    }),
    filterItemsList() {
      return this.$store.getters[this.getterKey] || [];
    },
    isAgentType() {
      return this.type === 'agent';
    },
    reportKeys() {
      return {
        CONVERSATIONS: 'conversations_count',
        ...(!this.isAgentType && {
          INCOMING_MESSAGES: 'incoming_messages_count',
        }),
        OUTGOING_MESSAGES: 'outgoing_messages_count',
        FIRST_RESPONSE_TIME: 'avg_first_response_time',
        RESOLUTION_TIME: 'avg_resolution_time',
        RESOLUTION_COUNT: 'resolutions_count',
        REPLY_TIME: 'reply_time',
      };
    },
  },
  mounted() {
    this.$store.dispatch(this.actionKey);
  },
  methods: {
    fetchAllData() {
      if (this.selectedFilter) {
        const { from, to, groupBy, businessHours } = this;
        this.$store.dispatch('fetchAccountSummary', {
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
          groupBy: groupBy.period,
          businessHours,
        });
        this.fetchConversationsList(0); // Fetch conversations on first page
        this.fetchChartData();
      }
    },
    fetchChartData() {
      Object.keys(this.reportKeys).forEach(async key => {
        try {
          const { from, to, groupBy, businessHours } = this;
          this.$store.dispatch('fetchAccountReport', {
            metric: this.reportKeys[key],
            from,
            to,
            type: this.type,
            id: this.selectedFilter.id,
            groupBy: groupBy.period,
            businessHours,
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    downloadReports() {
      this.showDropdown = false;
      const { from, to, type, businessHours } = this;
      const dispatchMethods = {
        agent: 'downloadAgentReports',
        label: 'downloadLabelReports',
        inbox: 'downloadInboxReports',
        team: 'downloadTeamReports',
      };
      if (dispatchMethods[type]) {
        const fileName = generateFileName({ type, to, businessHours });
        const params = { from, to, fileName, businessHours };
        this.$store.dispatch(dispatchMethods[type], params);
      }
    },
    downloadPDF() {
      this.showDropdown = false;
      const { from, to, groupBy, selectedFilter, selectedGroupByFilter } = this;

      const path = this.$route.path.split('/').pop();
      const labels = {
        inboxes: 'da Caixa de Entrada',
        agents: 'dos Agentes',
        teams: 'das Equipes',
        label: 'dos Marcadores',
      };

      const typesLabel = {
        label: 'Marcador',
        agent: 'Agente',
        team: 'Equipe',
        inbox: 'Caixa de Entrada',
      };

      this.$store.dispatch('generatePDF', {
        from,
        to,
        groupBy,
        selectedFilter,
        type: typesLabel[this.type] || this.type,
        selectedGroupByFilter,
        currentUser: JSON.parse(JSON.stringify(this.currentUser)),
        title: labels[path] || 'dos Agentes',
      });
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    onDateRangeChange({ from, to, groupBy }) {
      // do not track filter change on inital load
      if (this.from !== 0 && this.to !== 0) {
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: this.type,
        });
      }

      this.from = from;
      this.to = to;
      this.groupByfilterItemsList = this.fetchFilterItems(groupBy);
      const filterItems = this.groupByfilterItemsList.filter(
        item => item.id === this.groupBy.id
      );
      if (filterItems.length > 0) {
        this.selectedGroupByFilter = filterItems[0];
      } else {
        this.selectedGroupByFilter = this.groupByfilterItemsList[0];
        this.groupBy = GROUP_BY_FILTER[this.selectedGroupByFilter.id];
      }
      this.fetchAllData();
    },
    onFilterChange(payload) {
      if (payload) {
        this.selectedFilter = payload;
        this.fetchAllData();
      }
    },
    onGroupByFilterChange(payload) {
      this.groupBy = GROUP_BY_FILTER[payload.id];
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterType: 'groupBy',
        filterValue: this.groupBy?.period,
        reportType: this.type,
      });
    },
    fetchFilterItems(groupBy) {
      switch (groupBy) {
        case GROUP_BY_FILTER[2].period:
          return GROUP_BY_OPTIONS.WEEK.map(this.translateOptions);
        case GROUP_BY_FILTER[3].period:
          return GROUP_BY_OPTIONS.MONTH.map(this.translateOptions);
        case GROUP_BY_FILTER[4].period:
          return GROUP_BY_OPTIONS.YEAR.map(this.translateOptions);
        default:
          return GROUP_BY_OPTIONS.DAY.map(this.translateOptions);
      }
    },
    translateOptions(opts) {
      return { id: opts.id, groupBy: this.$t(opts.groupByKey) };
    },
    onBusinessHoursToggle(value) {
      this.businessHours = value;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterType: 'businessHours',
        filterValue: value,
        reportType: this.type,
      });
    },
    fetchConversationsList(page = 0) {
      if (this.selectedFilter) {
        const { from, to, groupBy, businessHours } = this;
        this.$store.dispatch('fetchConversationsList', {
          metric: 'conversations_count',
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
          page: page + 1, // API uses 1-based pagination
          groupBy: groupBy.period,
          businessHours,
        });
      }
    },
    onConversationsPageChange(pageIndex) {
      this.conversationsPageIndex = pageIndex;
      this.fetchConversationsList(pageIndex);
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <!-- <woot-button
      color-scheme="success"
      class-names="button--fixed-top"
      icon="arrow-download"
      >
      @click="downloadReports"
      {{ downloadButtonLabel }}
    </woot-button> -->
    <!-- Button to toggle dropdown -->
    <div style="background-color: #24262de6">
      <woot-button
        color-scheme="success"
        class-names="button--fixed-top"
        icon="arrow-download"
        @click="toggleDropdown"
      />

      <div v-if="showDropdown" class="dropdown-menu">
        <woot-button variant="clear" @click="downloadPDF">
          Baixar os gráficos em PDF
        </woot-button>
        <woot-button variant="clear" @click="downloadReports">
          {{ downloadButtonLabel }} em CSV
        </woot-button>
      </div>
    </div>
    <ReportFilters
      v-if="filterItemsList"
      :type="type"
      :filter-items-list="filterItemsList"
      :group-by-filter-items-list="groupByfilterItemsList"
      :selected-group-by-filter="selectedGroupByFilter"
      @date-range-change="onDateRangeChange"
      @filter-change="onFilterChange"
      @group-by-filter-change="onGroupByFilterChange"
      @business-hours-toggle="onBusinessHoursToggle"
    />
    <ReportContainer
      v-if="filterItemsList.length"
      :group-by="groupBy"
      :report-keys="reportKeys"
    />
    <!-- Conversations List Section -->
    <div v-if="filterItemsList.length" class="mt-8">
      <div class="mb-4">
        <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
          {{ $t('CONVERSATIONS_TABLE.HEADER.TITLE') || 'Conversas' }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400">
          {{
            $t('CONVERSATIONS_TABLE.DESCRIPTION') ||
            'Lista de conversas baseada nos filtros selecionados'
          }}
        </p>
      </div>
      <ConversationsListTable
        :page-index="conversationsPageIndex"
        @page-change="onConversationsPageChange"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.relative {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 50px; /* Ensures it appears right below the button */
  right: 9px;
  background: #24262de6;
  border: none;
  border-radius: 5px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  padding: 5px;
  min-width: 180px;
  z-index: 10;
  display: flex;
  flex-direction: column;
}

.woot-button {
  width: 100%;
  text-align: left;
}
</style>
